import { fromIsoString, toIsoString } from '../chat/iso-date';
import type {
  DatasetTriggerCriterion,
  DatasetTriggerRunMode,
} from '../dataset/dataset-trigger-criterion';
import type {
  SerializedWorkflowDatasetTrigger,
  WorkflowDatasetTriggerData,
} from './types';
import { WorkflowTrigger } from './workflow-trigger';
import type { WorkflowTriggerHydration } from './workflow-trigger';

/**
 * Standalone dataset trigger (`workflow_triggers_dataset`). It watches one
 * dataset for one kind of change; the evaluator fans a single dispatch out to
 * every workflow bound to it.
 *
 * Note what is deliberately absent: the dataset's kind. The criteria are
 * identical for csv, manual, sheet and warehouse, because how candidate rows
 * are produced belongs to the dataset, not to the trigger.
 */
export class WorkflowDatasetTrigger extends WorkflowTrigger<WorkflowDatasetTriggerData> {
  readonly kind = 'dataset' as const;

  private constructor(
    data: WorkflowDatasetTriggerData,
    hydration?: WorkflowTriggerHydration
  ) {
    super(data, hydration);
  }

  get name(): string {
    return this._data.name;
  }

  get datasetId(): string {
    return this._data.datasetId;
  }

  get criterion(): DatasetTriggerCriterion {
    return this._data.criterion;
  }

  get trackFields(): readonly string[] | null {
    return this._data.trackFields;
  }

  get filter(): unknown {
    return this._data.filter;
  }

  get processExistingRows(): boolean {
    return this._data.processExistingRows;
  }

  get runMode(): DatasetTriggerRunMode {
    return this._data.runMode;
  }

  get datasetTriggerData(): WorkflowDatasetTriggerData {
    return { ...this._data };
  }

  serialize(): SerializedWorkflowDatasetTrigger {
    return {
      id: this._data.id,
      ownerId: this._data.ownerId,
      ownerOrgId: this._data.ownerOrgId,
      name: this._data.name,
      datasetId: this._data.datasetId,
      criterion: this._data.criterion,
      trackFields: this._data.trackFields,
      filter: this._data.filter,
      processExistingRows: this._data.processExistingRows,
      runMode: this._data.runMode,
      enabled: this._data.enabled,
      createdAt: toIsoString(this._data.createdAt),
      updatedAt: toIsoString(this._data.updatedAt),
    };
  }

  static fromSerialized(
    serialized: SerializedWorkflowDatasetTrigger,
    hydration?: WorkflowTriggerHydration
  ): WorkflowDatasetTrigger {
    const data: WorkflowDatasetTriggerData = {
      id: serialized.id,
      ownerId: serialized.ownerId,
      ownerOrgId: serialized.ownerOrgId,
      name: serialized.name,
      datasetId: serialized.datasetId,
      criterion: serialized.criterion,
      trackFields: serialized.trackFields,
      filter: serialized.filter,
      processExistingRows: serialized.processExistingRows,
      runMode: serialized.runMode,
      enabled: serialized.enabled,
      createdAt: fromIsoString(serialized.createdAt),
      updatedAt: fromIsoString(serialized.updatedAt),
    };
    return new WorkflowDatasetTrigger(data, hydration);
  }
}
